/**
 * Custom hook for managing original values in DrivePanel
 *
 * Extracted from DrivePanel.tsx
 */

import { useState, useEffect } from 'react';
import type { Document as ApiDocument } from '../../../services/api';
import type {
  UseDriveOriginalValuesProps,
  UseDriveOriginalValuesReturn,
} from '../types/drive-panel.types';

/**
 * Extracts period from filename
 * From AdminPanel logic
 */
function extractPeriodFromFilename(filename: string): string | null {
  if (!filename) return null;

  const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
  const tempName = nameWithoutExt.replace(/ S\.A\./g, ' SA').replace(/ S\.p\.A\./g, ' SPA');
  const parts = tempName.split('_');
  const knownTypes = ['ANALISIS', 'FECU'];

  if (parts.length >= 2) {
    const typeIdx = parts.findIndex(part => knownTypes.includes(part));
    if (typeIdx > 0 && parts.length > typeIdx + 1) {
      return parts[typeIdx + 1];
    }
  }

  return null;
}

/**
 * Manages original values for company names and periods
 */
export function useDriveOriginalValues({
  privateDocuments,
  sharedDocuments,
}: UseDriveOriginalValuesProps): UseDriveOriginalValuesReturn {
  const [originalCompanyNames, setOriginalCompanyNames] = useState<Record<string, string>>({});
  const [originalPeriods, setOriginalPeriods] = useState<Record<string, string>>({});

  // Guardar valores originales cuando se cargan documentos (actualizado para incluir períodos)
  useEffect(() => {
    // Guardar valores originales de company_name
    setOriginalCompanyNames(prev => {
      const newOriginals: Record<string, string> = { ...prev };
      let hasChanges = false;

      [...privateDocuments, ...sharedDocuments].forEach(doc => {
        const currentOriginal = newOriginals[doc.id];
        const docCompanyName = doc.company_name || '';

        // Solo guardar si NO existe un valor original ya guardado
        if (!currentOriginal && docCompanyName) {
          newOriginals[doc.id] = docCompanyName;
          hasChanges = true;
        }
        // Si el valor original está vacío pero el documento tiene company_name, poblarlo
        else if (currentOriginal === '' && docCompanyName) {
          newOriginals[doc.id] = docCompanyName;
          hasChanges = true;
        }
      });

      return hasChanges ? newOriginals : prev;
    });

    // Guardar valores originales de períodos
    setOriginalPeriods(prev => {
      const newOriginals: Record<string, string> = { ...prev };
      let hasChanges = false;

      [...privateDocuments, ...sharedDocuments].forEach(doc => {
        // Solo procesar si no hay un valor original ya guardado
        if (!newOriginals[doc.id] || newOriginals[doc.id] === '') {
          // Intentar extraer el período del nombre del archivo primero
          const periodFromFilename = extractPeriodFromFilename(doc.original_filename || doc.name || '');

          if (periodFromFilename) {
            newOriginals[doc.id] = periodFromFilename;
            hasChanges = true;
          } else if (doc.document_period) {
            // Si no se puede extraer del nombre, usar el document_period actual
            newOriginals[doc.id] = doc.document_period;
            hasChanges = true;
          }
        }
      });

      return hasChanges ? newOriginals : prev;
    });
  }, [privateDocuments, sharedDocuments]); // Solo cuando cambian los documentos cargados

  return {
    originalCompanyNames,
    originalPeriods,
    extractPeriodFromFilename,
  };
}
